import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, Select } from '../../components/ui';
import { PrinterIcon } from '../../components/ui/Icons';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '../../components/ui/Icons';
import {
  TimelineHourHeader,
  TimelinePaintGrid,
} from '../../components/timeline-grid';
import { fetcher } from '../../services/fetcher';
import { putVesselOperationDay } from '../../services/mutations';

type OpType = { id: number; name: string; color: string };
type Segment = {
  operationTypeId: number;
  startMinute: number;
  endMinute: number;
  operationType?: OpType;
};
type DayRow = {
  workDate: string;
  vesselStatus: 'AT_SEA' | 'IN_PORT';
  notes: string | null;
  segments: Segment[];
};
type Sheet = {
  vesselId: number;
  vesselName: string;
  month: string;
  types: OpType[];
  days: DayRow[];
};

const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const nowParts = () => {
  const d = new Date();
  return {
    year: String(d.getFullYear()),
    month: String(d.getMonth() + 1).padStart(2, '0'),
  };
};

const toPeriodYm = (year: string, month: string) => `${year}-${month}`;

function weekdayLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  return WEEKDAYS[d.getUTCDay()] ?? '';
}

const SLOT_W = 26;
const HOURS_W = 48 * SLOT_W;
const DATE_COL = 72;
const STATUS_COL = 100;
const NOTES_COL = 160;

const VesselOperationsSchedulePage: React.FC<{ lockedVesselId?: number }> = ({
  lockedVesselId,
}) => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();
  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>(
    '/crew/lookups',
    fetcher,
    { revalidateOnFocus: false },
  );
  const initial = nowParts();
  const urlVessel = searchParams.get('vesselId')?.trim() || '';
  const urlMonth = searchParams.get('month')?.trim() || '';
  const urlYm = /^\d{4}-\d{2}$/.test(urlMonth) ? urlMonth : '';
  const [vesselId, setVesselId] = useState(
    lockedVesselId ? String(lockedVesselId) : urlVessel,
  );
  const [year, setYear] = useState(urlYm ? urlYm.slice(0, 4) : initial.year);
  const [month, setMonth] = useState(urlYm ? urlYm.slice(5, 7) : initial.month);
  const [applied, setApplied] = useState<{ vesselId: string; month: string } | null>(() => {
    const vid = lockedVesselId ? String(lockedVesselId) : urlVessel;
    if (vid && urlYm) return { vesselId: vid, month: urlYm };
    if (lockedVesselId) {
      return { vesselId: String(lockedVesselId), month: toPeriodYm(initial.year, initial.month) };
    }
    return null;
  });
  const [typesCollapsed, setTypesCollapsed] = useState(false);

  useEffect(() => {
    if (!urlVessel || !urlYm) return;
    const vid = lockedVesselId ? String(lockedVesselId) : urlVessel;
    setVesselId(vid);
    setYear(urlYm.slice(0, 4));
    setMonth(urlYm.slice(5, 7));
    setApplied({ vesselId: vid, month: urlYm });
  }, [urlVessel, urlYm, lockedVesselId]);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const years: Array<{ value: string; label: string }> = [];
    for (let y = current + 1; y >= current - 10; y -= 1) {
      years.push({ value: String(y), label: String(y) });
    }
    return years;
  }, []);

  const key = applied
    ? `/vessel-operations/sheet?vesselId=${applied.vesselId}&month=${applied.month}`
    : null;
  const { data, mutate, isLoading } = useSWR<Sheet>(key, fetcher, { revalidateOnFocus: false });

  const vesselOptions = useMemo(
    () => (lookups?.vessels ?? []).map((v) => ({ value: String(v.id), label: v.vesselName })),
    [lookups?.vessels],
  );

  const menuOptions = (data?.types ?? []).map((t) => ({
    id: String(t.id),
    label: t.name,
    color: t.color,
  }));

  const segmentCount = useMemo(
    () => (data?.days ?? []).reduce((n, d) => n + d.segments.length, 0),
    [data],
  );

  const saveDay = async (day: DayRow, patch: Partial<DayRow> & { segments?: Segment[] }) => {
    if (!applied) return;
    const next = { ...day, ...patch };
    await putVesselOperationDay(Number(applied.vesselId), day.workDate, {
      vesselStatus: next.vesselStatus,
      notes: next.notes,
      segments: (next.segments ?? []).map((s) => ({
        operationTypeId: s.operationTypeId,
        startMinute: s.startMinute,
        endMinute: s.endMinute,
      })),
    });
    await mutate();
  };

  const paintDay = async (
    day: DayRow,
    range: { startMinute: number; endMinute: number },
    optionId: string | null,
  ) => {
    const remaining: Segment[] = [];
    for (const s of day.segments) {
      if (s.endMinute <= range.startMinute || s.startMinute >= range.endMinute) {
        remaining.push(s);
        continue;
      }
      if (s.startMinute < range.startMinute) {
        remaining.push({ ...s, endMinute: range.startMinute });
      }
      if (s.endMinute > range.endMinute) {
        remaining.push({ ...s, startMinute: range.endMinute });
      }
    }
    if (optionId) {
      remaining.push({
        operationTypeId: Number(optionId),
        startMinute: range.startMinute,
        endMinute: range.endMinute,
      });
    }
    remaining.sort((a, b) => a.startMinute - b.startMinute);
    await saveDay(day, { segments: remaining });
  };

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-2', colors.background)}>
      <div
        className={cn(
          'flex shrink-0 flex-wrap items-center gap-2 rounded-xl border px-3 py-2',
          colors.border,
          colors.surface,
        )}
      >
        {!lockedVesselId && (
          <div className="w-[11rem]">
            <Select
              value={vesselId}
              onChange={(e) => setVesselId(e.target.value)}
              options={vesselOptions}
              placeholder="Vessel"
              showClearButton={false}
            />
          </div>
        )}
        <div className="w-[6.5rem]">
          <Select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            options={yearOptions}
            showClearButton={false}
          />
        </div>
        <div className="w-[9.5rem]">
          <Select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            options={MONTH_OPTIONS}
            showClearButton={false}
          />
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            const vid = lockedVesselId ? String(lockedVesselId) : vesselId;
            if (!vid) return;
            setApplied({ vesselId: vid, month: toPeriodYm(year, month) });
          }}
        >
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>

        {data && (
          <>
            <div className={cn('mx-1 hidden h-6 w-px sm:block', 'bg-black/10 dark:bg-white/10')} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className={cn('truncate text-sm font-semibold', colors.text)}>
                  {data.vesselName}
                </span>
                <span className={cn('truncate text-xs', colors.textMuted)}>
                  {data.month} · {data.days.length} days · {segmentCount} ops
                </span>
              </div>
            </div>
            {applied ? (
              <Link
                to={`/vessel-operations/report?vesselId=${encodeURIComponent(applied.vesselId)}&month=${encodeURIComponent(applied.month)}`}
                className="shrink-0"
              >
                <Button type="button" size="sm" variant="secondary">
                  <PrinterIcon className="h-4 w-4" />
                  Print
                </Button>
              </Link>
            ) : null}
          </>
        )}
      </div>

      {isLoading && (
        <p className={cn('shrink-0 text-sm', colors.textMuted)}>Loading schedule…</p>
      )}

      {!applied && !isLoading && (
        <div
          className={cn(
            'flex flex-1 items-center justify-center rounded-xl border border-dashed text-sm',
            colors.border,
            colors.textMuted,
          )}
        >
          Choose vessel and month, then Search.
        </div>
      )}

      {data && (
        <div className="flex min-h-0 flex-1 gap-2">
          <aside
            className={cn(
              'flex shrink-0 flex-col overflow-hidden rounded-xl border transition-[width]',
              typesCollapsed ? 'w-11' : 'w-44 sm:w-52',
              colors.border,
              colors.surface,
            )}
          >
            <div
              className={cn(
                'flex shrink-0 items-center border-b px-1.5 py-1.5',
                typesCollapsed ? 'justify-center' : 'justify-between px-2.5',
                colors.border,
              )}
            >
              {!typesCollapsed && (
                <>
                  <span
                    className={cn(
                      'text-[10px] font-semibold uppercase tracking-wide',
                      colors.textSecondary,
                    )}
                  >
                    Types
                  </span>
                  <span className={cn('text-[10px] tabular-nums', colors.textMuted)}>
                    {data.types.length}
                  </span>
                </>
              )}
              <button
                type="button"
                onClick={() => setTypesCollapsed((v) => !v)}
                className={cn(
                  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
                  colors.textMuted,
                  'hover:bg-black/[0.05] dark:hover:bg-white/[0.06]',
                  !typesCollapsed && 'ml-1',
                )}
                title={typesCollapsed ? 'Expand types' : 'Collapse types'}
                aria-label={typesCollapsed ? 'Expand types' : 'Collapse types'}
                aria-expanded={!typesCollapsed}
              >
                {typesCollapsed ? (
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                ) : (
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                )}
              </button>
            </div>

            {typesCollapsed ? (
              <div className="min-h-0 flex-1 overflow-y-auto px-1.5 py-1.5">
                {(data.types ?? []).map((t) => (
                  <div
                    key={t.id}
                    className="mb-1.5 flex justify-center"
                    title={t.name}
                  >
                    <span
                      className="h-3 w-3 rounded-sm ring-1 ring-black/10 dark:ring-white/10"
                      style={{ backgroundColor: t.color }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                  {(data.types ?? []).map((t) => (
                    <div
                      key={t.id}
                      className={cn(
                        'mb-0.5 flex items-center gap-2 rounded-lg px-2 py-1.5 text-[12px]',
                        colors.text,
                      )}
                    >
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-sm ring-1 ring-black/10 dark:ring-white/10"
                        style={{ backgroundColor: t.color }}
                      />
                      <span className="min-w-0 truncate">{t.name}</span>
                    </div>
                  ))}
                  {data.types.length === 0 && (
                    <p className={cn('px-2 py-4 text-xs', colors.textMuted)}>
                      No operation types yet.
                    </p>
                  )}
                </div>
                <div
                  className={cn(
                    'shrink-0 border-t px-2.5 py-1.5 text-[10px]',
                    colors.border,
                    colors.textMuted,
                  )}
                >
                  Paint colors feed WRH sheet backgrounds
                </div>
              </>
            )}
          </aside>

          <div
            className={cn(
              'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border',
              colors.border,
              colors.surface,
            )}
          >
            <div className="min-h-0 flex-1 overflow-auto">
              <div
                className={cn(
                  'sticky top-0 z-10 flex min-w-max items-stretch border-b bg-white/95 backdrop-blur-sm dark:bg-zinc-950/95',
                  colors.border,
                )}
              >
                <div
                  className={cn(
                    'sticky left-0 z-20 flex shrink-0 items-end px-2 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wide',
                    colors.textSecondary,
                    'bg-white/95 dark:bg-zinc-950/95',
                  )}
                  style={{ width: DATE_COL }}
                >
                  Day
                </div>
                <div
                  className={cn(
                    'flex shrink-0 items-end px-1 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wide',
                    colors.textSecondary,
                  )}
                  style={{ width: STATUS_COL }}
                >
                  Status
                </div>
                <div className="min-w-0 flex-1 px-1 pb-1 pt-1.5" style={{ minWidth: HOURS_W }}>
                  <TimelineHourHeader slotMinutes={30} fillWidth cellMinWidth={SLOT_W} />
                </div>
                <div
                  className={cn(
                    'flex shrink-0 items-end border-l border-black/[0.08] px-2 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wide dark:border-white/[0.1]',
                    colors.textSecondary,
                  )}
                  style={{ width: NOTES_COL }}
                >
                  Notes
                </div>
              </div>

              {data.days.map((day, idx) => {
                const slotBg: Array<string | null> = Array.from({ length: 48 }, () => null);
                for (const s of day.segments) {
                  const color =
                    s.operationType?.color ||
                    data.types.find((t) => t.id === s.operationTypeId)?.color;
                  for (let m = s.startMinute; m < s.endMinute; m += 30) {
                    const i = Math.floor(m / 30);
                    if (i >= 0 && i < 48) slotBg[i] = color ?? null;
                  }
                }
                const isWeekend = [0, 6].includes(
                  new Date(`${day.workDate}T12:00:00Z`).getUTCDay(),
                );
                return (
                  <div
                    key={day.workDate}
                    className={cn(
                      'flex min-w-max items-stretch border-b border-black/[0.04] dark:border-white/[0.04]',
                      isWeekend && 'bg-amber-500/[0.04]',
                      idx % 2 === 1 && !isWeekend && 'bg-black/[0.015] dark:bg-white/[0.02]',
                    )}
                  >
                    <div
                      className={cn(
                        'sticky left-0 z-[5] flex shrink-0 flex-col justify-center border-l-2 px-2 py-0.5',
                        isWeekend
                          ? 'border-l-transparent bg-amber-50 dark:bg-amber-950/40'
                          : cn('border-l-transparent', colors.surface),
                      )}
                      style={{ width: DATE_COL }}
                    >
                      <div className="flex items-baseline gap-1">
                        <div className={cn('text-sm font-semibold tabular-nums leading-none', colors.text)}>
                          {day.workDate.slice(8)}
                        </div>
                        <div className={cn('text-[10px] leading-none', colors.textMuted)}>
                          {weekdayLabel(day.workDate)}
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center px-1" style={{ width: STATUS_COL }}>
                      <select
                        value={day.vesselStatus}
                        onChange={(e) =>
                          void saveDay(day, {
                            vesselStatus: e.target.value as 'AT_SEA' | 'IN_PORT',
                          })
                        }
                        className={cn(
                          'h-7 w-full rounded-md border px-1 text-[11px] outline-none',
                          colors.border,
                          colors.surface,
                          colors.text,
                        )}
                      >
                        <option value="AT_SEA">At sea</option>
                        <option value="IN_PORT">In port</option>
                      </select>
                    </div>
                    <div className="min-w-0 flex-1 px-1 py-0.5" style={{ minWidth: HOURS_W }}>
                      <TimelinePaintGrid
                        slotMinutes={30}
                        segments={[]}
                        slotBackgrounds={slotBg}
                        cellMinWidth={SLOT_W}
                        fillWidth
                        menuOptions={menuOptions}
                        onPaint={(range, optionId) => void paintDay(day, range, optionId)}
                      />
                    </div>
                    <div
                      className={cn(
                        'flex shrink-0 items-center border-l border-black/[0.08] px-2 dark:border-white/[0.1]',
                        isWeekend
                          ? 'bg-amber-50/90 dark:bg-amber-950/30'
                          : 'bg-zinc-50/90 dark:bg-zinc-900/90',
                      )}
                      style={{ width: NOTES_COL }}
                    >
                      <input
                        type="text"
                        defaultValue={day.notes ?? ''}
                        key={`${day.workDate}:${day.notes ?? ''}`}
                        maxLength={500}
                        placeholder="Add note…"
                        onBlur={(e) => {
                          const next = e.target.value.trim() || null;
                          if (next !== (day.notes || null)) {
                            void saveDay(day, { notes: next });
                          }
                        }}
                        className={cn(
                          'h-7 w-full rounded-md bg-transparent px-1.5 text-[11px] outline-none transition-colors',
                          'placeholder:text-zinc-400 dark:placeholder:text-zinc-500',
                          'hover:bg-black/[0.03] focus:bg-white focus:ring-1 focus:ring-sky-500/35 dark:hover:bg-white/[0.04] dark:focus:bg-zinc-900',
                          colors.text,
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VesselOperationsSchedulePage;
